/**
 * Video format types and enums for the video editor
 */

import { z } from "zod";

// ISize schema for validation
export const ISizeSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
  type: z.string().optional(),
  name: z.string().optional(),
});

export type ISize = z.infer<typeof ISizeSchema>;

export enum VideoFormat {
  LANDSCAPE_16_9 = "16:9",
  PORTRAIT_9_16 = "9:16", 
  SQUARE_1_1 = "1:1",
  LANDSCAPE_4_3 = "4:3",
  PORTRAIT_3_4 = "3:4",
  LANDSCAPE_21_9 = "21:9",
  PORTRAIT_9_21 = "9:21",
  LANDSCAPE_5_4 = "5:4",
  PORTRAIT_4_5 = "4:5",
}

export interface ResizeValue {
  width: number;
  height: number;
  name: string;
}

export interface VideoFormatConfig {
  label: string;
  icon: string;
  description: string;
  value: ResizeValue;
}

// Default video format options with their aspect ratios
export const DEFAULT_VIDEO_FORMATS: readonly VideoFormatConfig[] = [
  {
    label: "16:9",
    icon: "landscape",
    description: "YouTube ads",
    value: {
      width: 1920,
      height: 1080,
      name: VideoFormat.LANDSCAPE_16_9,
    },
  },
  {
    label: "9:16",
    icon: "portrait",
    description: "TikTok, YouTube Shorts",
    value: {
      width: 1080,
      height: 1920,
      name: VideoFormat.PORTRAIT_9_16,
    },
  },
  {
    label: "1:1",
    icon: "square",
    description: "Instagram, Facebook posts",
    value: {
      width: 1080,
      height: 1080,
      name: VideoFormat.SQUARE_1_1,
    },
  },
  {
    label: "4:3",
    icon: "landscape",
    description: "Traditional TV, presentations",
    value: {
      width: 1024,
      height: 768,
      name: VideoFormat.LANDSCAPE_4_3,
    },
  },
  {
    label: "3:4",
    icon: "portrait",
    description: "Instagram stories, vertical presentations",
    value: {
      width: 768,
      height: 1024,
      name: VideoFormat.PORTRAIT_3_4,
    },
  },
  {
    label: "21:9",
    icon: "landscape",
    description: "Ultrawide, cinematic",
    value: {
      width: 2560,
      height: 1080,
      name: VideoFormat.LANDSCAPE_21_9,
    },
  },
  {
    label: "9:21",
    icon: "portrait",
    description: "Ultra-tall, vertical cinematic",
    value: {
      width: 1080,
      height: 2560,
      name: VideoFormat.PORTRAIT_9_21,
    },
  },
  {
    label: "5:4",
    icon: "landscape",
    description: "Square-ish landscape",
    value: {
      width: 1280,
      height: 1024,
      name: VideoFormat.LANDSCAPE_5_4,
    },
  },
  {
    label: "4:5",
    icon: "portrait",
    description: "Instagram portrait posts",
    value: {
      width: 1024,
      height: 1280,
      name: VideoFormat.PORTRAIT_4_5,
    },
  },
] as const;

// Basic video formats (commonly used ones)
export const BASIC_VIDEO_FORMATS: readonly VideoFormatConfig[] = [
  {
    label: "16:9",
    icon: "landscape",
    description: "YouTube ads",
    value: {
      width: 1920,
      height: 1080,
      name: VideoFormat.LANDSCAPE_16_9,
    },
  },
  {
    label: "9:16",
    icon: "portrait",
    description: "TikTok, YouTube Shorts",
    value: {
      width: 1080,
      height: 1920,
      name: VideoFormat.PORTRAIT_9_16,
    },
  },
  {
    label: "1:1",
    icon: "square",
    description: "Instagram, Facebook posts",
    value: {
      width: 1080,
      height: 1080,
      name: VideoFormat.SQUARE_1_1,
    },
  },
] as const;

// Type for video format names
export type VideoFormatName = VideoFormat;

// Type for video format configuration
export type VideoFormatOption = VideoFormatConfig;

// Helper type for format calculation
export interface FormatCalculationOptions {
  maxWidth: number;
  maxHeight: number;
  format: VideoFormatConfig;
}

// Helper function to calculate optimal dimensions for a format
// Max resolution is height x height from the plan
export function calculateFormatDimensions(
  format: VideoFormatConfig,
  _maxWidth: number, // Unused - we only use height as max dimension
  maxHeight: number
): ResizeValue {
  const { name } = format.value;
  const maxDimension = maxHeight; // Use height as the maximum dimension
  
  switch (name) {
    case VideoFormat.LANDSCAPE_16_9: {
      // Landscape: use max dimension as width, calculate height maintaining 16:9 ratio
      let width = maxDimension;
      let height = Math.floor((maxDimension * 9) / 16);
      
      // If calculated height exceeds max dimension, use max dimension and calculate width
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 16) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_9_16: {
      // Portrait: use max dimension as height, calculate width maintaining 9:16 ratio
      let height = maxDimension;
      let width = Math.floor((maxDimension * 9) / 16);
      
      // If calculated width exceeds max dimension, use max dimension and calculate height
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 16) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.SQUARE_1_1: {
      // Square: use max dimension for both width and height
      return { width: maxDimension, height: maxDimension, name };
    }
    case VideoFormat.LANDSCAPE_4_3: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 3) / 4);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 4) / 3);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_3_4: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 3) / 4);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 4) / 3);
      }
      return { width, height, name };
    }
    case VideoFormat.LANDSCAPE_21_9: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 9) / 21);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 21) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_9_21: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 9) / 21);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 21) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.LANDSCAPE_5_4: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 4) / 5);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 5) / 4);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_4_5: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 4) / 5);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 5) / 4);
      }
      return { width, height, name };
    }
    default:
      return format.value;
  }
}
